import { useMemo, useState } from 'react';
import {
  ArrowsClockwiseIcon,
  CircleNotchIcon,
  FlowArrowIcon,
} from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { SystemEntity } from '@erp/shared-types';
import type {
  GetEntityWorkflowContextResponse,
  StepRunResponse,
} from '@erp/rpc';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { VersionBadge } from '@/pages/authenticated/workflows/components/VersionBadge';
import { useToast } from '@/context/ToastContext';
import {
  useEntityWorkflowContext,
  useRestartFromStep,
  useRetryStep,
} from '@/api/rpc/hooks/workflows/useWorkflowDefinitions';
import { cn } from '@/lib/utils';
import { T, Var, useGT } from 'gt-react';

type EntityWorkflowContext = NonNullable<
  GetEntityWorkflowContextResponse['context']
>;
type EntityWorkflowStep = EntityWorkflowContext['steps'][number];
type EntityWorkflowEdge = EntityWorkflowContext['edges'][number];

interface RerunWorkflowStepDropdownProps {
  entityType: SystemEntity;
  entityId: string;
  isAdmin?: boolean;
}

interface PendingAction {
  type: 'restart' | 'retry';
  step: EntityWorkflowStep;
}

function canRetryStep(stepRun: StepRunResponse | null | undefined): boolean {
  return stepRun?.status === 'failed' || stepRun?.status === 'timed_out';
}

function getStepStatusVariant(
  status: StepRunResponse['status'],
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'completed':
      return 'default';
    case 'failed':
    case 'timed_out':
      return 'destructive';
    case 'running':
    case 'queued':
      return 'secondary';
    default:
      return 'outline';
  }
}

/**
 * BFS to find all downstream step IDs from a given step.
 */
function getDownstreamStepIds(
  stepId: string,
  edges: EntityWorkflowEdge[],
): Set<string> {
  const downstream = new Set<string>();
  const queue = [stepId];
  while (queue.length > 0) {
    const current = queue.shift();
    if (current === undefined) break;
    for (const edge of edges) {
      if (edge.sourceStepId === current && !downstream.has(edge.targetStepId)) {
        downstream.add(edge.targetStepId);
        queue.push(edge.targetStepId);
      }
    }
  }
  return downstream;
}

export function RerunWorkflowStepDropdown({
  entityType,
  entityId,
  isAdmin: isAdminProp = false,
}: RerunWorkflowStepDropdownProps) {
  const gt = useGT();
  const { showToast } = useToast();
  const { data, isLoading, isFetching } = useEntityWorkflowContext(
    entityType,
    entityId,
  );
  const retryStepMutation = useRetryStep();
  const restartFromStepMutation = useRestartFromStep();

  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null,
  );
  const [highlightedStepId, setHighlightedStepId] = useState<string | null>(
    null,
  );

  const workflowContext = data?.context ?? null;
  const executedSteps = useMemo(
    () => workflowContext?.steps ?? [],
    [workflowContext?.steps],
  );
  const edges = useMemo(
    () => workflowContext?.edges ?? [],
    [workflowContext?.edges],
  );
  const isActionPending =
    retryStepMutation.isPending || restartFromStepMutation.isPending;

  // Precompute downstream map for all steps
  const downstreamMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const step of executedSteps) {
      map.set(
        step.workflowStepId,
        getDownstreamStepIds(step.workflowStepId, edges),
      );
    }
    return map;
  }, [executedSteps, edges]);

  // Current set of highlighted downstream step IDs
  const highlightedDownstream = highlightedStepId
    ? (downstreamMap.get(highlightedStepId) ?? new Set<string>())
    : new Set<string>();

  // Downstream step names for the confirmation dialog
  const pendingDownstreamNames = useMemo(() => {
    if (!pendingAction || pendingAction.type === 'retry') return [];
    const downstreamIds = downstreamMap.get(pendingAction.step.workflowStepId);
    if (!downstreamIds || downstreamIds.size === 0) return [];
    return executedSteps
      .filter((s) => downstreamIds.has(s.workflowStepId))
      .map((s) => s.stepName);
  }, [pendingAction, downstreamMap, executedSteps]);

  if (!isLoading && !isFetching && !workflowContext) {
    return null;
  }

  const confirmTitle =
    pendingAction?.type === 'retry'
      ? gt('Retry Step', {
          context: 'workflow step retry confirmation dialog title',
        })
      : gt('Restart From Step', {
          context: 'workflow restart from step confirmation dialog title',
        });
  const confirmText =
    pendingAction?.type === 'retry'
      ? gt('Retry Step', { context: 'confirm button retry workflow step' })
      : gt('Restart', { context: 'confirm button restart workflow from step' });

  async function handleConfirmAction() {
    if (!pendingAction || !workflowContext) {
      return;
    }

    try {
      if (pendingAction.type === 'retry') {
        const latestStepRun = pendingAction.step.latestStepRun;
        if (!latestStepRun || !canRetryStep(latestStepRun)) {
          showToast({
            title: gt('Step cannot be retried', {
              context: 'toast when workflow step cannot be retried',
            }),
            message: gt(
              'Only steps with failed or timed_out status can be retried.',
              { context: 'toast body explaining retry eligibility' },
            ),
            color: 'error',
          });
          return;
        }

        await retryStepMutation.mutateAsync({
          workflowRunId: workflowContext.workflowRun.id,
          stepRunId: latestStepRun.id,
        });

        showToast({
          title: gt('Step retry initiated', {
            context: 'toast when workflow step retry queued',
          }),
          message: gt('Queued retry for "{stepName}".', {
            context: 'toast body with step name',
            stepName: pendingAction.step.stepName,
          }),
          color: 'success',
        });
      } else {
        await restartFromStepMutation.mutateAsync({
          workflowRunId: workflowContext.workflowRun.id,
          workflowStepId: pendingAction.step.workflowStepId,
        });

        showToast({
          title: gt('Workflow restart initiated', {
            context: 'toast when workflow restart from step queued',
          }),
          message: gt('Restarted workflow from "{stepName}".', {
            context: 'toast body with step name for workflow restart',
            stepName: pendingAction.step.stepName,
          }),
          color: 'success',
        });
      }

      setPendingAction(null);
    } catch (error) {
      showToast({
        title: gt('Action failed', {
          context: 'toast when workflow rerun action fails',
        }),
        message:
          error instanceof Error
            ? error.message
            : gt('Failed to rerun workflow step.', {
                context: 'generic error when workflow step rerun fails',
              }),
        color: 'error',
      });
    }
  }

  // Non-admin: simple "Rerun Workflow" button that restarts from the root step
  if (!isAdminProp) {
    const handleRerunWorkflow = () => {
      if (executedSteps.length === 0) return;
      // Find the root step (no incoming edges) rather than relying on array position
      const incomingTargetIds = new Set(edges.map((e) => e.targetStepId));
      const rootStep =
        executedSteps.find((s) => !incomingTargetIds.has(s.workflowStepId)) ??
        executedSteps[0];
      setPendingAction({ type: 'restart', step: rootStep });
    };

    return (
      <>
        <Button
          variant="outline"
          size="sm"
          disabled={
            isActionPending ||
            isLoading ||
            isFetching ||
            executedSteps.length === 0
          }
          onClick={handleRerunWorkflow}
        >
          {isActionPending ? (
            <CircleNotchIcon className="h-4 w-4 animate-spin" />
          ) : (
            <ArrowsClockwiseIcon className="h-4 w-4" />
          )}
          <T context="button to rerun entire workflow from start">
            Rerun Workflow
          </T>
        </Button>

        <ConfirmDialog
          open={pendingAction !== null}
          onClose={() => setPendingAction(null)}
          onConfirm={handleConfirmAction}
          title={gt('Rerun Workflow', {
            context: 'confirm dialog title rerun entire workflow',
          })}
          confirmText={gt('Rerun', {
            context: 'confirm button rerun workflow',
          })}
          loading={isActionPending}
        >
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              <T context="confirm dialog body for non-admin workflow rerun">
                Rerun the entire workflow from the beginning? All steps will be
                re-executed.
              </T>
            </p>
          </div>
        </ConfirmDialog>
      </>
    );
  }

  // Admin: full step-level dropdown
  return (
    <>
      <DropdownMenu
        modal={false}
        onOpenChange={(open) => {
          if (!open) setHighlightedStepId(null);
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={isActionPending}>
            {isLoading || isFetching ? (
              <CircleNotchIcon className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowsClockwiseIcon className="h-4 w-4" />
            )}
            <T context="dropdown trigger for workflow-created entity actions">
              Created by Workflow
            </T>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[280px]">
          <DropdownMenuLabel>
            <div className="flex items-center gap-2">
              <div className="truncate font-medium flex-1">
                {workflowContext?.workflowDefinitionName ||
                  gt('Workflow', {
                    context:
                      'fallback label when workflow definition name missing',
                  })}
              </div>
              {workflowContext?.workflowRun && (
                <VersionBadge
                  workflowId={workflowContext.workflowRun.workflowId}
                  version={
                    workflowContext.workflowRun.workflowDefinitionVersion
                  }
                  isPinned={
                    workflowContext.workflowRun.workflowDefinitionIsPinned
                  }
                  changelog={
                    workflowContext.workflowRun.workflowDefinitionChangelog
                  }
                  compact
                />
              )}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {workflowContext?.workflowRun && (
            <>
              <DropdownMenuItem asChild>
                <Link
                  to="/workflows/$workflowId/runs/$workflowRunId"
                  params={{
                    workflowId: workflowContext.workflowRun.workflowId,
                    workflowRunId: workflowContext.workflowRun.id,
                  }}
                  className="gap-2"
                >
                  <FlowArrowIcon className="h-4 w-4" />
                  <T context="menu item linking to the workflow run that produced this entity">
                    View workflow run (latest)
                  </T>
                </Link>
              </DropdownMenuItem>
              {workflowContext.parentWorkflowRun && (
                <DropdownMenuItem asChild>
                  <Link
                    to="/workflows/$workflowId/runs/$workflowRunId"
                    params={{
                      workflowId: workflowContext.parentWorkflowRun.workflowId,
                      workflowRunId: workflowContext.parentWorkflowRun.id,
                    }}
                    className="gap-2"
                  >
                    <FlowArrowIcon className="h-4 w-4" />
                    <T context="menu item linking to the parent workflow run that spawned this run">
                      View parent workflow run
                    </T>
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
            </>
          )}
          {isLoading || isFetching ? (
            <DropdownMenuItem disabled>
              <CircleNotchIcon className="h-4 w-4 animate-spin" />
              <T context="loading workflow steps in dropdown">
                Loading steps...
              </T>
            </DropdownMenuItem>
          ) : executedSteps.length === 0 ? (
            <DropdownMenuItem disabled>
              <T context="empty state when no workflow steps executed">
                No executed steps found
              </T>
            </DropdownMenuItem>
          ) : (
            executedSteps.map((step) => {
              const isDownstream = highlightedDownstream.has(
                step.workflowStepId,
              );
              return (
                <DropdownMenuSub
                  key={step.workflowStepId}
                  onOpenChange={(open) => {
                    setHighlightedStepId(open ? step.workflowStepId : null);
                  }}
                >
                  <DropdownMenuSubTrigger
                    className={cn(
                      'gap-2',
                      isDownstream && 'bg-blue-50 dark:bg-blue-950/40',
                    )}
                  >
                    <span className="truncate">{step.stepName}</span>
                    {step.latestStepRun &&
                    step.latestStepRun.status !== 'completed' ? (
                      <Badge
                        variant={getStepStatusVariant(
                          step.latestStepRun.status,
                        )}
                        className="text-[10px]"
                      >
                        {step.latestStepRun.status}
                      </Badge>
                    ) : null}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-[220px]">
                    <DropdownMenuItem
                      disabled={isActionPending}
                      onClick={() =>
                        setPendingAction({ type: 'restart', step })
                      }
                    >
                      <T context="menu item restart workflow from this step">
                        Restart from step
                      </T>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={
                        isActionPending || !canRetryStep(step.latestStepRun)
                      }
                      onClick={() => setPendingAction({ type: 'retry', step })}
                    >
                      <T context="menu item retry failed workflow step">
                        Retry step
                      </T>
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              );
            })
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        onConfirm={handleConfirmAction}
        title={confirmTitle}
        confirmText={confirmText}
        loading={isActionPending}
      >
        <div className="space-y-3">
          {pendingAction?.type === 'retry' ? (
            <p className="text-sm text-muted-foreground">
              <T context="confirm retry workflow step with step name in quotes">
                Retry the latest failed attempt for &ldquo;
                <Var>{pendingAction.step.stepName}</Var>
                &rdquo;?
              </T>
            </p>
          ) : pendingAction ? (
            <>
              <p className="text-sm text-muted-foreground">
                <T context="confirm restart workflow from named step">
                  Restart workflow from &ldquo;
                  <Var>{pendingAction.step.stepName}</Var>
                  &rdquo;?
                </T>
              </p>
              {pendingDownstreamNames.length > 0 && (
                <div className="rounded-md border p-3 bg-muted/50">
                  <p className="text-xs font-medium mb-1.5">
                    <T context="label listing downstream steps that will re-run">
                      The following downstream steps will also re-run:
                    </T>
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-0.5">
                    {pendingDownstreamNames.map((name) => (
                      <li key={name} className="flex items-center gap-1.5">
                        <span className="h-1 w-1 rounded-full bg-muted-foreground" />
                        {name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : null}
        </div>
      </ConfirmDialog>
    </>
  );
}
